import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      minWidth: "600px",
      maxWidth: "800px",
      width: "90%",
    },
    // Screen reader only class for accessibility
    "& .sr-only": {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: 0,
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0,
    },
  },
  dialogTitle: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
  titleText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  dialogContent: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
  },
  dialogActions: {
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  formGrid: {
    marginBottom: theme.spacing(2),
  },
  textField: {
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  formControl: {
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiSelect-root": {
      color: theme.palette.text.primary,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  loadingText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },
}));

// Yup validation schema for company form
const CompanySchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(150, "Nome deve ter no máximo 50 caracteres")
    .required("Nome é obrigatório"),
  email: Yup.string()
    .email("Email deve ter um formato válido")
    .required("Email é obrigatório"),
  phone: Yup.string()
    .nullable()
    .notRequired(),
  planId: Yup.string()
    .nullable()
    .notRequired(),
  document: Yup.string()
    .nullable()
    .notRequired(),
  recurrence: Yup.string()
    .nullable()
    .notRequired(),
  dueDate: Yup.date()
    .nullable()
    .notRequired()
    .typeError("Data de vencimento inválida"),
  password: Yup.string()
    .when('$mode', {
      is: 'create',
      then: Yup.string()
        .min(5, "Senha deve ter pelo menos 5 caracteres")
        .required("Senha é obrigatória para criar empresa"),
      otherwise: Yup.string().notRequired()
    }),
  paymentMethod: Yup.string()
    .nullable()
    .notRequired(),
});

const CompanyModal = ({
  open,
  mode,
  company,
  plans,
  loading,
  onClose,
  onSubmit,
}) => {
  const classes = useStyles();
  
  // Refs for focus management and accessibility
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const submitButtonRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    paymentMethod: "",
    generateInvoice: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Initialize form data when modal opens or company changes
  useEffect(() => {
    if (open) {
      if (mode === "edit" && company) {
        setFormData({
          id: company.id,
          name: company.name || "",
          email: company.email || "",
          phone: company.phone || "",
          planId: company.planId || "",
          status: company.status !== false,
          dueDate: company.dueDate && moment(company.dueDate).isValid()
            ? moment(company.dueDate).format("YYYY-MM-DD")
            : "",
          recurrence: company.recurrence || "",
          password: "",
          document: company.document || "",
          paymentMethod: company.paymentMethod || "",
          generateInvoice: company.generateInvoice !== false,
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: "",
          email: "",
          phone: "",
          planId: "",
          status: true,
          dueDate: "",
          recurrence: "",
          password: "",
          document: "",
          paymentMethod: "",
          generateInvoice: true,
        });
      }
      setErrors({});
      
      // Focus management - focus first field when modal opens
      if (firstFieldRef.current) {
        setTimeout(() => {
          firstFieldRef.current.focus();
        }, 100);
      }
    }
  }, [open, mode, company]);

  // Handle keyboard navigation and accessibility
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;

      // ESC key to close modal
      if (event.key === 'Escape' && !submitting) {
        event.preventDefault();
        handleClose();
        return;
      }

      // Trap focus within modal
      if (event.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        );
        
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      }

      // Enter key to submit form when focused on form elements (except textarea)
      if (event.key === 'Enter' && !submitting) {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.tagName !== 'TEXTAREA' && activeElement.tagName !== 'BUTTON') {
          // Only submit if we're in a form field and form is valid
          const form = activeElement.closest('form');
          if (form && !Object.keys(errors).length) {
            event.preventDefault();
            handleSubmit(event);
          }
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Announce modal opening to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `Modal ${getModalTitle()} aberto. Use Tab para navegar e Escape para fechar.`;
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        if (document.body.contains(announcement)) {
          document.body.removeChild(announcement);
        }
      }, 1000);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, submitting, errors]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = async () => {
    try {
      // Validate using Yup schema with context for conditional validation
      await CompanySchema.validate(formData, { 
        abortEarly: false,
        context: { mode }
      });
      setErrors({});
      return true;
    } catch (validationErrors) {
      const newErrors = {};
      
      if (validationErrors.inner) {
        validationErrors.inner.forEach(error => {
          newErrors[error.path] = error.message;
        });
      } else {
        // Single error case
        newErrors[validationErrors.path] = validationErrors.message;
      }
      
      setErrors(newErrors);
      
      // Announce errors to screen readers
      const errorCount = Object.keys(newErrors).length;
      announceErrors(errorCount);
      
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setSubmitting(true);
    try {
      const dataToSend = { ...formData };

      // Clean empty fields that shouldn't be sent
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === "" && key !== "password") {
          delete dataToSend[key];
        }
      });

      // Handle date field - convert to proper format or set to null
      if (dataToSend.dueDate && moment(dataToSend.dueDate).isValid()) {
        dataToSend.dueDate = moment(dataToSend.dueDate).format("YYYY-MM-DD");
      } else {
        dataToSend.dueDate = null;
      }

      // Convert planId to number if it exists
      if (dataToSend.planId && dataToSend.planId !== "") {
        dataToSend.planId = parseInt(dataToSend.planId, 10);
      } else {
        delete dataToSend.planId;
      }

      // Ensure status is boolean
      dataToSend.status = Boolean(dataToSend.status);

      // Ensure generateInvoice is boolean
      dataToSend.generateInvoice = Boolean(dataToSend.generateInvoice);

      await onSubmit(dataToSend);
    } catch (error) {
      console.error("Error submitting form:", error);
      // Error handling is done in parent component
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  // Announce form errors to screen readers
  const announceErrors = (errorCount) => {
    if (errorCount > 0) {
      const announcement = `Formulário contém ${errorCount} erro${errorCount > 1 ? 's' : ''}. Verifique os campos destacados.`;
      // Create a temporary element for screen reader announcement
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'assertive');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      announcer.textContent = announcement;
      document.body.appendChild(announcer);
      
      // Focus first field with error for better UX
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const errorElement = dialogRef.current?.querySelector(`[name="${firstErrorField}"], [aria-label*="${firstErrorField}"]`);
        if (errorElement && errorElement.focus) {
          setTimeout(() => errorElement.focus(), 100);
        }
      }
      
      setTimeout(() => {
        if (document.body.contains(announcer)) {
          document.body.removeChild(announcer);
        }
      }, 1000);
    }
  };

  const getModalTitle = () => {
    return mode === "edit" ? "Editar Empresa" : "Nova Empresa";
  };

  const isFieldDisabled = loading || submitting;

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
      aria-labelledby="company-modal-title"
      aria-describedby="company-modal-description"
      role="dialog"
      aria-modal="true"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography 
          variant="h6" 
          className={classes.titleText}
          id="company-modal-title"
          component="h2"
        >
          {getModalTitle()}
        </Typography>
        <div 
          id="company-modal-description" 
          className="sr-only"
        >
          {mode === "edit" 
            ? `Formulário para editar dados da empresa ${company?.name || ''}`
            : "Formulário para criar uma nova empresa"
          }. Use Tab para navegar entre os campos e Escape para fechar.
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
            <Typography className={classes.loadingText}>
              Carregando...
            </Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit} noValidate role="form">
            <Grid container spacing={2} className={classes.formGrid}>
              {/* Name Field */}
              <Grid item xs={12} md={6}>
                <TextField
                  ref={firstFieldRef}
                  fullWidth
                  name="name"
                  label={i18n.t("companies.table.name")}
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  required
                  disabled={isFieldDisabled}
                  error={!!errors.name}
                  helperText={errors.name}
                 
                  variant="outlined"
                  aria-describedby={errors.name ? "name-error" : "name-helper"}
                  aria-invalid={!!errors.name}
                  inputProps={{
                    'aria-label': 'Nome da empresa',
                    'aria-required': 'true',
                    'maxLength': 50
                  }}
                />
                <div id="name-helper" className="sr-only">
                  Campo obrigatório. Nome da empresa, máximo 50 caracteres.
                </div>
                {errors.name && (
                  <div id="name-error" className="sr-only" role="alert">
                    Erro no campo nome: {errors.name}
                  </div>
                )}
              </Grid>

              {/* Email Field */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="email"
                  label={i18n.t("companies.table.email")}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  disabled={isFieldDisabled}
                  error={!!errors.email}
                  helperText={errors.email}
                 
                  variant="outlined"
                  aria-describedby={errors.email ? "email-error" : "email-helper"}
                  aria-invalid={!!errors.email}
                  inputProps={{
                    'aria-label': 'Email da empresa',
                    'aria-required': 'true',
                    'autoComplete': 'email'
                  }}
                />
                <div id="email-helper" className="sr-only">
                  Campo obrigatório. Email válido da empresa.
                </div>
                {errors.email && (
                  <div id="email-error" className="sr-only" role="alert">
                    Erro no campo email: {errors.email}
                  </div>
                )}
              </Grid>

              {/* Phone Field */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="phone"
                  label={i18n.t("companies.table.phone")}
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  disabled={isFieldDisabled}
                 
                  variant="outlined"
                  aria-describedby="phone-helper"
                  inputProps={{
                    'aria-label': 'Telefone da empresa (opcional)',
                    'autoComplete': 'tel'
                  }}
                />
                <div id="phone-helper" className="sr-only">
                  Campo opcional. Telefone de contato da empresa.
                </div>
              </Grid>

              {/* Plan Field */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth className={classes.formControl} variant="outlined">
                  <InputLabel id="plan-select-label">{i18n.t("companies.table.plan")}</InputLabel>
                  <Select
                    labelId="plan-select-label"
                    name="planId"
                    value={formData.planId}
                    onChange={(e) => handleInputChange("planId", e.target.value)}
                    disabled={isFieldDisabled}
                    label={i18n.t("companies.table.plan")}
                    aria-label="Selecionar plano da empresa"
                    aria-describedby="plan-helper-text"
                  >
                    <MenuItem value="" aria-label="Nenhum plano selecionado">
                      <em>Nenhum</em>
                    </MenuItem>
                    {plans.map((plan) => (
                      <MenuItem 
                        key={plan.id} 
                        value={plan.id}
                        aria-label={`Plano ${plan.name}`}
                      >
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <div id="plan-helper-text" className="sr-only">
                    Selecione um plano para a empresa ou deixe em branco
                  </div>
                </FormControl>
              </Grid>

              {/* Due Date Field */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="dueDate"
                  label={i18n.t("companies.table.dueDate")}
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={isFieldDisabled}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                 
                  variant="outlined"
                  aria-describedby="duedate-helper"
                  inputProps={{
                    'aria-label': 'Data de vencimento da empresa (opcional)'
                  }}
                />
                <div id="duedate-helper" className="sr-only">
                  Campo opcional. Data de vencimento do plano da empresa.
                </div>
              </Grid>

              {/* Document Field */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="document"
                  label={i18n.t("companies.table.document")}
                  value={formData.document}
                  onChange={(e) => handleInputChange("document", e.target.value)}
                  disabled={isFieldDisabled}
                 
                  variant="outlined"
                  aria-describedby="document-helper"
                  inputProps={{
                    'aria-label': 'Documento da empresa (opcional)'
                  }}
                />
                <div id="document-helper" className="sr-only">
                  Campo opcional. CNPJ ou CPF da empresa.
                </div>
              </Grid>

              {/* Recurrence Field */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth className={classes.formControl} variant="outlined">
                  <InputLabel id="recurrence-select-label">{i18n.t("companies.table.recurrence")}</InputLabel>
                  <Select
                    labelId="recurrence-select-label"
                    name="recurrence"
                    value={formData.recurrence}
                    onChange={(e) => handleInputChange("recurrence", e.target.value)}
                    disabled={isFieldDisabled}
                    label={i18n.t("companies.table.recurrence")}
                    aria-label="Selecionar recorrência de cobrança"
                    aria-describedby="recurrence-helper"
                  >
                    <MenuItem value="" aria-label="Nenhuma recorrência">
                      <em>Nenhuma</em>
                    </MenuItem>
                    <MenuItem value="MENSAL" aria-label="Cobrança mensal">{i18n.t("companies.table.monthly")}</MenuItem>
                    <MenuItem value="BIMESTRAL" aria-label="Cobrança bimestral">{i18n.t("companies.table.bimonthly")}</MenuItem>
                    <MenuItem value="TRIMESTRAL" aria-label="Cobrança trimestral">{i18n.t("companies.table.quarterly")}</MenuItem>
                    <MenuItem value="SEMESTRAL" aria-label="Cobrança semestral">{i18n.t("companies.table.semester")}</MenuItem>
                    <MenuItem value="ANUAL" aria-label="Cobrança anual">{i18n.t("companies.table.yearly")}</MenuItem>
                  </Select>
                  <div id="recurrence-helper" className="sr-only">
                    Campo opcional. Frequência de cobrança do plano.
                  </div>
                </FormControl>
              </Grid>

              {/* Generate Invoice Field */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="generateInvoice"
                      checked={formData.generateInvoice}
                      onChange={(e) => handleInputChange("generateInvoice", e.target.checked)}
                      disabled={isFieldDisabled}
                      color="primary"
                      inputProps={{
                        'aria-label': 'Gerar fatura automaticamente para esta empresa'
                      }}
                    />
                  }
                  label="Gerar Fatura Automaticamente"
                  aria-describedby="generateInvoice-helper"
                />
                <div id="generateInvoice-helper" className="sr-only">
                  Marque esta opção se deseja que o sistema gere faturas automaticamente para esta empresa
                </div>
              </Grid>

              {/* Password Field - Only for create mode */}
              {mode === "create" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="password"
                    label={i18n.t("companies.table.password")}
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    disabled={isFieldDisabled}
                    error={!!errors.password}
                    helperText={errors.password || "Senha para o primeiro usuário admin da empresa"}

                    variant="outlined"
                    required
                    aria-describedby={errors.password ? "password-error" : "password-helper"}
                    aria-invalid={!!errors.password}
                    inputProps={{
                      'aria-label': 'Senha para o primeiro usuário admin',
                      'aria-required': 'true',
                      'minLength': 5,
                      'autoComplete': 'new-password'
                    }}
                  />
                  <div id="password-helper" className="sr-only">
                    Campo obrigatório. Senha deve ter pelo menos 5 caracteres.
                  </div>
                  {errors.password && (
                    <div id="password-error" className="sr-only" role="alert">
                      Erro no campo senha: {errors.password}
                    </div>
                  )}
                </Grid>
              )}
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          color="default"
          aria-label="Cancelar e fechar modal"
        >
          Cancelar
        </Button>
        <ButtonWithSpinner
          ref={submitButtonRef}
          loading={submitting}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          aria-label={mode === "edit" ? "Salvar alterações da empresa" : "Criar nova empresa"}
          aria-describedby="submit-button-description"
        >
          {mode === "edit" ? "Atualizar" : "Criar"}
        </ButtonWithSpinner>
        <div id="submit-button-description" className="sr-only">
          {submitting 
            ? "Processando solicitação, aguarde..." 
            : mode === "edit" 
              ? "Clique para salvar as alterações da empresa"
              : "Clique para criar a nova empresa"
          }
        </div>
      </DialogActions>
    </Dialog>
  );
};

CompanyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  company: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    planId: PropTypes.number,
    status: PropTypes.bool,
    dueDate: PropTypes.string,
    recurrence: PropTypes.string,
    document: PropTypes.string,
    paymentMethod: PropTypes.string,
    generateInvoice: PropTypes.bool,
  }),
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

CompanyModal.defaultProps = {
  company: null,
  loading: false,
};

export default CompanyModal;